import styled, { css } from 'styled-components';
import Icons from '../../Icons';
import { ButtonSizes, ButtonShapes } from './button-consts';

export const pix = size => `${size}px`;

export const sizes = {
  [ButtonSizes.SMALL]: {
    height: 32,
    width: 112
  },
  [ButtonSizes.LARGE]: {
    height: 36,
    width: 112
  },
  [ButtonSizes.XLARGE]: {
    height: 44,
    width: 147
  }
};

const fontSizes = {
  [ButtonSizes.SMALL]: 14,
  [ButtonSizes.LARGE]: 14,
  [ButtonSizes.XLARGE]: 16
};

export const BasicButton = styled.button.attrs({
  className: 'dsc-button'
})`
  display: flex;
  align-items: center;
  position: relative;
  border: none;
  outline: none;
  box-shadow: none;
  font-family: var(--app-font) Averta, sans-serif;
  font-weight: bold;
  height: ${({ $buttonSize }) => pix($buttonSize?.height)};

  ${({ $size }) =>
    ($size === ButtonSizes.SMALL &&
      css`
        padding: 0px 16px;
      `) ||
    ($size === ButtonSizes.LARGE &&
      css`
        padding: 0px 16px;
      `) ||
    ($size === ButtonSizes.XLARGE &&
      css`
        padding: 0px 16px;
      `)};

  &:disabled {
    opacity: var(--disabled-opacity, 1);
  }

  ${({ $shape }) =>
    ($shape === ButtonShapes.FILLED &&
      css`
        min-width: ${({ $buttonSize }) => pix($buttonSize?.width)};
        && {
          background: ${({ $loading }) =>
            $loading ? 'var(--button-filled-hover-bg-color)' : 'var(--button-filled-bg-color)'};
          color: ${({ $loading }) => ($loading ? 'var(--button-filled-hover-color)' : 'var(--button-filled-color)')};
          .icon-wrapper {
            svg {
              color: ${({ $loading }) =>
                $loading ? 'var(--button-filled-hover-color)' : 'var(--button-filled-color)'};
              transition: color 0.3s;
            }
          }
          border-radius: var(--button-radius);
          transition: color 0.3s, background 0.3s ease-in;

          &:hover {
            &:not([disabled]) {
              cursor: pointer;
              background: var(--button-filled-hover-bg-color);
              color: var(--button-filled-hover-color);
              .icon-wrapper {
                svg {
                  color: var(--button-filled-hover-color);
                }
              }
            }
          }

          &:disabled {
            color: var(--button-filled-disabled-color);
            .icon-wrapper {
              svg {
                color: var(--button-filled-disabled-color);
              }
            }
            background: var(--button-filled-disabled-bg-color);
          }

          &:active {
            &:not([disabled]) {
              transform: scale(0.96);
            }
          }
        }
      `) ||
    ($shape === ButtonShapes.LINKED &&
      css`
        font-weight: 400;

        && {
          background: transparent;
          color: var(--button-linked-color);
          .icon-wrapper {
            svg {
              color: var(--button-linked-color);
            }
          }
          transition: all 0.15s;
          text-decoration: var(--button-linked-decoration);

          &:hover {
            &:not([disabled]) {
              background: transparent;
              text-decoration: var(--button-linked-hover-decoration);
              cursor: pointer;
            }
          }

          &:disabled {
            text-decoration: unset;
            color: var(--button-linked-disabled-color);
            .icon-wrapper {
              svg {
                color: var(--button-linked-disabled-color);
              }
            }
          }
          &:active {
            &:not([disabled]) {
              transform: scale(0.96);
            }
          }
        }
      `) ||
    ($shape === ButtonShapes.OUTLINED &&
      css`
        min-width: ${({ $buttonSize }) => pix($buttonSize?.width)};

        && {
          background-color: ${({ $loading }) =>
            $loading ? 'var(--button-outlined-hover-bg-color)' : 'var(--button-outlined-bg-color)'};
          color: ${({ $loading }) =>
            $loading ? 'var(--button-outlined-hover-color)' : 'var(--button-outlined-color)'};
          .icon-wrapper {
            svg {
              color: ${({ $loading }) =>
                $loading ? 'var(--button-outlined-hover-color)' : 'var(--button-outlined-color)'};
            }
          }

          border-radius: var(--button-radius);
          box-shadow: ${({ $loading }) => !$loading && '0 0 0 1px var(--button-outlined-border-color)'};
          transition: all 0.15s;

          &:hover {
            &:not([disabled]) {
              cursor: pointer;
              box-shadow: 0 0 0 1px var(--button-outlined-border-color);
              color: var(--button-outlined-hover-color);
              .icon-wrapper {
                svg {
                  color: var(--button-outlined-hover-color);
                }
              }
              background-color: var(--button-outlined-hover-bg-color);
            }
          }

          &:disabled {
            background: var(--button-outlined-disabled-bg-color);
            box-shadow: 0 0 0 1px var(--button-outlined-disabled-border-color, var(--button-outlined-disabled-color));
            color: var(--button-outlined-disabled-color);
            .icon-wrapper {
              svg {
                color: var(--button-outlined-disabled-color);
              }
            }
          }
          &:active {
            &:not([disabled]) {
              transform: scale(0.96);
              box-shadow: unset;
              background-color: var(--button-outlined-hover-bg-color);
            }
          }
        }
      `) ||
    ($shape === ButtonShapes.NAKED &&
      css`
        && {
          background: transparent;
          color: var(--button-naked-color);
          .icon-wrapper {
            svg {
              color: var(--button-naked-color);
            }
          }
          transition: all 0.15s;

          &:hover {
            &:not([disabled]) {
              background: transparent;
              text-decoration: unset;
              text-decoration: underline;
              cursor: pointer;
            }
          }

          &:disabled {
            text-decoration: unset;
            color: var(--button-naked-disabled-color);
            .icon-wrapper {
              svg {
                color: var(--button-naked-disabled-color);
              }
            }
          }
          &:active {
            &:not([disabled]) {
              transform: scale(0.96);
            }
          }
        }
      `) ||
    ($shape === ButtonShapes.NAKED_LIGHT &&
      css`
        font-weight: 400;

        && {
          background: transparent;
          color: var(--button-naked-color);
          .icon-wrapper {
            svg {
              color: var(--button-naked-color);
            }
          }
          transition: all 0.15s;
          text-transform: uppercase;

          &:hover {
            &:not([disabled]) {
              background: transparent;
              text-decoration: unset;
              text-decoration: underline;
              cursor: pointer;
            }
          }

          &:disabled {
            text-decoration: unset;
            color: var(--button-naked-disabled-color);
            .icon-wrapper {
              svg {
                color: var(--button-naked-disabled-color);
              }
            }
          }
          &:active {
            &:not([disabled]) {
              transform: scale(0.96);
            }
          }
        }
      `)}
`;
export const TitleWrapper = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
`;

export const Title = styled.span.attrs({ className: 'dsc-button-title' })`
  ${({ $size }) =>
    ($size === ButtonSizes.SMALL &&
      css`
        font-size: ${fontSizes[$size]}px;
        letter-spacing: 0.5px;
        padding: 10px 0px;
        line-height: 12px;
      `) ||
    ($size === ButtonSizes.LARGE &&
      css`
        font-size: ${fontSizes[$size]}px;
        letter-spacing: 0.5px;
        padding: 12px 0px;
        line-height: 12px;
      `) ||
    ($size === ButtonSizes.XLARGE &&
      css`
        font-size: ${fontSizes[$size]}px;
        letter-spacing: 0.5px;
        text-transform: uppercase;
        padding: 16px 0px;
        line-height: 12px;
      `)};
`;

export const IconWrapper = styled.span`
  display: flex;
  align-items: center;
  gap: 8px;
  .dsc-icon-wrapper {
    font-size: 16px;
  }
`;

export const InProgress = styled(Icons.InProgress)`
  && {
    position: absolute;
    right: -4px;
    top: -6px;
  }
`;
